import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllAdminUsers, isSuperAdmin, getCurrentUserEmail } from '../services/AdminUserApi';
import AdminUserTable from './RoleManagement/AdminUserTable';
import CreateAdminModal from './RoleManagement/CreateAdminModal';
import SearchFilterBar from './RoleManagement/SearchFilterBar';
import Header from '../common/Header';
import './RoleManagement/RoleManagement.css';


const RoleManagement = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
const indexOfLastUser = currentPage * rowsPerPage;
const indexOfFirstUser = indexOfLastUser - rowsPerPage;
const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);

const totalPages = Math.ceil(filteredUsers.length / rowsPerPage);

  // Check if user is SUPER_ADMIN
  useEffect(() => {
    if (!isSuperAdmin()) {
      setAccessDenied(true);
      setLoading(false);
    }
  }, []);

  // Fetch admin users on mount
  useEffect(() => {
    if (!accessDenied) {
      fetchUsers();
    }
  }, [accessDenied]);

  // Apply filters whenever users or filters change
  useEffect(() => {
    applyFilters();
  }, [users, searchQuery, roleFilter, statusFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, roleFilter, statusFilter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAllAdminUsers();
      setUsers(data);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.error || 
                          'Failed to load admin users';
      setError(errorMessage);
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...users];

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(user =>
        user.email.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Role filter
    if (roleFilter !== 'ALL') {
      filtered = filtered.filter(user => user.role === roleFilter);
    }

    // Status filter
    if (statusFilter !== 'ALL') {
      filtered = filtered.filter(user => user.status === statusFilter);
    }

    setFilteredUsers(filtered);
  };

  const handleCreateSuccess = () => {
    setIsCreateModalOpen(false);
    fetchUsers(); // Refresh list
  };

  const adminCount = users.filter(u => u.role === 'ADMIN').length;
  const superAdminCount = users.filter(u => u.role === 'SUPER_ADMIN').length;
  const activeCount = users.filter(u => u.status === 'ACTIVE').length;

  // Access Denied UI
  if (accessDenied) {
    return (
      <div className="container-fluid d-flex flex-column w-100 p-0 m-0 role-management-page">
        <Header />
        <div className="role-management">
          <div className="access-denied-container">
            <div className="access-denied-icon">🚫</div>
            <h2>Access Denied</h2>
            <p>You do not have permission to access this page.</p>
            <p className="access-denied-note">
              Only SUPER_ADMIN users can manage admin roles.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Loading UI
  if (loading) {
    return (
      <div className="container-fluid d-flex flex-column w-100 p-0 m-0 role-management-page">
        <Header />
        <div className="role-management">
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading admin users...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error UI
  if (error) {
    return (
      <div className="container-fluid d-flex flex-column w-100 p-0 m-0 role-management-page">
        <Header />
        <div className="role-management">
          <div className="error-container">
            <div className="error-icon">⚠️</div>
            <h3>Error Loading Admin Users</h3>
            <p className="error-message">{error}</p>
            <button onClick={fetchUsers} className="btn-primary">
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid d-flex flex-column w-100 p-0 m-0 role-management-page">
      <Header />
      <div className="role-management">
        {/* Header Row with Back Button */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
          {/* Circular Back Button */}
          <button 
            type="button"
            style={{
              border: "none",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "6px 12px",
              fontSize: "0.9rem",
              color: "#000",
              backgroundColor: "transparent",
              cursor: "pointer",
            }}
            onClick={() => navigate('/AdminHome/dashboard')}
            title="Back to Dashboard"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="black"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="grey"
                strokeWidth="2"
                fill="none"
              />
              <polyline
                points="12,8 8,12 12,16"
                stroke="grey"
                strokeWidth="2"
                fill="none"
              />
            </svg>
            <span style={{ color: "grey" }}>Back</span>
          </button>

          {/* Header */}
          <div className="page-header" style={{ flex: 1, marginBottom: 0 }}>
            <div>
              <h1>Role Management</h1>
              <p className="subtitle">
                Manage system administrators and super administrators
              </p>
            </div>
            <button 
              className="btn-primary"
              onClick={() => setIsCreateModalOpen(true)}
            >
              <span className="btn-icon">+</span> Add New Admin
            </button>
          </div>
        </div>

      {/* Stats */}
      <div className="stats-bar">
        <div className="stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <span className="stat-label">Total Users</span>
            <span className="stat-value">{users.length}</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">⭐</div>
          <div className="stat-content">
            <span className="stat-label">Super Admins</span>
            <span className="stat-value">{superAdminCount}</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">👤</div>
          <div className="stat-content">
            <span className="stat-label">Admins</span>
            <span className="stat-value">{adminCount}</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <span className="stat-label">Active</span>
            <span className="stat-value">{activeCount}</span>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <SearchFilterBar
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        roleFilter={roleFilter}
        setRoleFilter={setRoleFilter}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
      />

      {/* Table */}
      <AdminUserTable
         users={currentUsers}
         onRefresh={fetchUsers}
         currentUserEmail={getCurrentUserEmail()}
       />

       {/* Numbered Pagination */}
       <div className="pagination-container">

         {/* Previous Button */}
         <button
           className="pagination-btn"
           disabled={currentPage === 1}
           onClick={() => setCurrentPage(prev => prev - 1)}
         >
           Prev
         </button> 
         
         {/* Page Numbers with Ellipsis */}
         <div className="pagination-numbers">
           {(() => {
             const pageNumbers = [];
             
             if (totalPages <= 7) {
               // Show all pages if 7 or fewer
               for (let i = 1; i <= totalPages; i++) {
                 pageNumbers.push(i);
               }
             } else {
               // Show first page
               pageNumbers.push(1);
               
               if (currentPage <= 4) {
                 // Near the beginning: show 1, 2, 3, 4, 5, ..., last
                 for (let i = 2; i <= 5; i++) {
                   pageNumbers.push(i);
                 }
                 pageNumbers.push('...');
                 pageNumbers.push(totalPages);
               } else if (currentPage >= totalPages - 3) {
                 // Near the end: show 1, ..., last-4, last-3, last-2, last-1, last
                 pageNumbers.push('...');
                 for (let i = totalPages - 4; i <= totalPages; i++) {
                   pageNumbers.push(i);
                 }
               } else {
                 // In the middle: show 1, ..., current-1, current, current+1, ..., last
                 pageNumbers.push('...');
                 pageNumbers.push(currentPage - 1);
                 pageNumbers.push(currentPage);
                 pageNumbers.push(currentPage + 1);
                 pageNumbers.push('...');
                 pageNumbers.push(totalPages);
               }
             }
             
             return pageNumbers.map((page, index) => {
               if (page === '...') {
                 return (
                   <span key={`ellipsis-${index}`} className="pagination-ellipsis">
                     ...
                   </span>
                 );
               }
               return (
                 <button
                   key={page}
                   className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                   onClick={() => setCurrentPage(page)}
                 >
                   {page}
                 </button>
               );
             });
           })()}
         </div>

         {/* Next Button */}
         <button
           className="pagination-btn"
           disabled={currentPage === totalPages}
           onClick={() => setCurrentPage(prev => prev + 1)}
         >
           Next
         </button>
       </div>



      {/* Create Modal */}
      {isCreateModalOpen && (
        <CreateAdminModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={handleCreateSuccess}
        />
      )}
      </div>
    </div>
  );
};

export default RoleManagement;

